// src/pages/agent/Finances.js
// Full P&L for HomeIQa. Admin sees company-wide; contract agents see
// their personal numbers. Lead submitters never get this page.
//
// Revenue is computed from leads with status 'Assigned' or 'Closed'
// using the per-deal commission split. "Lost pipeline" surfaces leads
// the team worked but were closed Dead — visibility on what walked away.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAgentAuth } from '../../lib/auth';
import { aggregateEarnings, splitDeal, DEFAULT_HOMEIQA_PCT } from '../../lib/earnings';
import { fmtMoney } from '../../lib/calc';
import {
  PageHeader, Card, Btn, Pill,
  BURG, BURG_DK, GOLD, GOLD_PALE, CREAM, MID, LIGHT, BORDER
} from '../../components/agent/UI';

export default function Finances() {
  const { profile } = useAgentAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [byMonth, setByMonth] = useState({});
  const [closedDeals, setClosedDeals] = useState([]);
  const [activeDeals, setActiveDeals] = useState([]);
  const [deadDeals, setDeadDeals] = useState([]);
  const [agentBreakdown, setAgentBreakdown] = useState([]);
  const [submitterBreakdown, setSubmitterBreakdown] = useState([]);
  const [totals, setTotals] = useState(null);

  useEffect(() => {
    if (!profile?.uid) return;
    (async () => {
      try {
        // Resolve the pipeline owner once. All leads live in the admin's
        // pipeline (per the open-queue model). Admin reads everything;
        // contract agents read the same pipeline but filter for the
        // leads they've claimed (assignedTo.uid === self).
        let pipelineUid = null;
        if (profile.role === 'admin') {
          pipelineUid = profile.uid;
        } else if (profile.role === 'agent') {
          pipelineUid = profile.invitedByUid || null;
          if (!pipelineUid) {
            try {
              const aSnap = await getDocs(query(
                collection(db, 'agents'),
                where('role', '==', 'admin'),
                where('status', '==', 'active'),
                limit(1)
              ));
              if (!aSnap.empty) pipelineUid = aSnap.docs[0].id;
            } catch {}
          }
          if (!pipelineUid) pipelineUid = profile.uid;
        }

        // For the per-agent breakdown table (admin only), we still need
        // to know every active contract agent so we can bucket their
        // claimed deals. We build that map up front; the deal loop
        // below routes earnings to the assignee's row.
        const isAdmin = profile.role === 'admin';
        let agentDirectory = [];
        if (isAdmin) {
          const aSnap = await getDocs(collection(db, 'agents'));
          agentDirectory = aSnap.docs
            .map(d => ({ uid: d.id, ...d.data() }))
            .filter(a => a.role !== 'lead_submitter' && a.status === 'active');
        } else {
          agentDirectory = [{ uid: profile.uid, ...profile }];
        }
        const agentByUid = new Map();
        for (const a of agentDirectory) {
          agentByUid.set(a.uid, {
            uid: a.uid,
            name: a.name || a.email,
            role: a.role,
            defaultPct: typeof a.defaultCommissionToHomeIQa === 'number' ? a.defaultCommissionToHomeIQa : DEFAULT_HOMEIQA_PCT,
            totalFees: 0, totalHomeIQa: 0, totalAgent: 0, dealCount: 0,
            projectedFees: 0, projectedHomeIQa: 0, projectedAgentCut: 0, projectedCount: 0
          });
        }

        const monthMap = {};
        const closed = [];
        const active = [];
        const dead = [];
        let totalFees = 0, totalHomeIQa = 0, totalAgent = 0, dealCount = 0;
        let projectedFees = 0, projectedHomeIQa = 0, projectedAgent = 0;
        let lostFees = 0, deadCount = 0;

        // Walk every lead in the admin's pipeline ONCE. Earnings are
        // credited to the lead's assignedTo agent. If unassigned, the
        // owner (admin) keeps it. Contract agents only see deals
        // where assignedTo.uid === self.
        const lSnap = await getDocs(collection(db, 'agents', pipelineUid, 'leads'));
        for (const d of lSnap.docs) {
          const lead = { id: d.id, ...d.data() };

          // Per-Lead-Agent (submitter) counts use the unfiltered pass
          // so admin sees everybody who's sourcing leads. We collect
          // them in a separate map after this loop. Submitter counts
          // are below.
          const assigneeUid = lead.assignedTo?.uid || null;

          // Shared team model — no per-agent assignment, so everyone sees
          // the whole pipeline's finances together.

          // Pick the right default-split — assignee's if known, else
          // the pipeline owner's, else the global default.
          const splitOwner = agentByUid.get(assigneeUid) || agentByUid.get(pipelineUid) || null;
          const defaultPct = splitOwner?.defaultPct ?? DEFAULT_HOMEIQA_PCT;
          const split = splitDeal(lead, defaultPct);

          const fee = split.fee;
          const status = (lead.status || '').toLowerCase();
          const closedAt = lead.closedAt?.toMillis?.() || lead.updatedAt?.toMillis?.() || lead.createdAt?.toMillis?.() || 0;
          const ownerName = (assigneeUid && agentByUid.get(assigneeUid)?.name) || (lead.assignedTo?.name) || 'Unassigned';

          if (status === 'closed' || status === 'assigned') {
            if (fee > 0) {
              closed.push({ ...lead, _ownerUid: assigneeUid || pipelineUid, _ownerName: ownerName, ...split, closedAt });
              const dt = new Date(closedAt);
              const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
              monthMap[key] = monthMap[key] || { homeIQa: 0, agent: 0, fees: 0, count: 0 };
              monthMap[key].homeIQa += split.homeIQaCut;
              monthMap[key].agent += split.agentCut;
              monthMap[key].fees += split.fee;
              monthMap[key].count += 1;
              totalFees += split.fee;
              totalHomeIQa += split.homeIQaCut;
              totalAgent += split.agentCut;
              dealCount += 1;
              if (assigneeUid && agentByUid.has(assigneeUid)) {
                const row = agentByUid.get(assigneeUid);
                row.totalFees += split.fee;
                row.totalHomeIQa += split.homeIQaCut;
                row.totalAgent += split.agentCut;
                row.dealCount += 1;
              }
            }
          } else if (status === 'negotiating' || status === 'under contract') {
            if (fee > 0) {
              active.push({ ...lead, _ownerUid: assigneeUid || pipelineUid, _ownerName: ownerName, ...split });
              projectedFees += split.fee;
              projectedHomeIQa += split.homeIQaCut;
              projectedAgent += split.agentCut;
              if (assigneeUid && agentByUid.has(assigneeUid)) {
                const row = agentByUid.get(assigneeUid);
                row.projectedFees += split.fee;
                row.projectedHomeIQa += split.homeIQaCut;
                row.projectedAgentCut += split.agentCut;
                row.projectedCount += 1;
              }
            }
          } else if (status === 'dead') {
            dead.push({ ...lead, _ownerUid: assigneeUid || pipelineUid, _ownerName: ownerName, ...split, closedAt: lead.closedAt?.toMillis?.() || lead.updatedAt?.toMillis?.() });
            lostFees += split.fee;
            deadCount += 1;
          }
        }

        const perAgent = isAdmin
          ? Array.from(agentByUid.values()).filter(r => r.dealCount > 0 || r.projectedCount > 0)
          : [];

        closed.sort((a, b) => b.closedAt - a.closedAt);
        active.sort((a, b) => (b.calc?.assignmentFee || 0) - (a.calc?.assignmentFee || 0));
        dead.sort((a, b) => (b.closedAt || 0) - (a.closedAt || 0));

        // Per-Lead-Agent (submitter) breakdown — admin only. Walks the
        // pipeline once more to bucket every lead by its submitter
        // (the Lead Agent who sourced it via share link / public form
        // / direct entry). Contract agents don't see this card.
        let submitters = [];
        if (isAdmin) {
          const submitterMap = new Map();
          const lSnap2 = await getDocs(collection(db, 'agents', pipelineUid, 'leads'));
          for (const d of lSnap2.docs) {
            const lead = d.data();
            const sb = lead.submittedBy;
            const submitterName = sb?.name || (lead.createdBy ? 'Direct entry' : 'Unknown');
            const key = sb?.uid || sb?.email || submitterName;
            const status = (lead.status || '').toLowerCase();
            const fee = Number(lead.calc?.assignmentFee) || 0;
            if (!submitterMap.has(key)) {
              submitterMap.set(key, {
                key,
                name: submitterName,
                source: sb?.source || (lead.createdBy ? 'manual' : 'unknown'),
                slug: sb?.slug || null,
                phone: sb?.phone || null,
                email: sb?.email || null,
                submitted: 0,
                active: 0, closed: 0, dead: 0,
                totalFees: 0, totalPayout: 0
              });
            }
            const row = submitterMap.get(key);
            row.submitted += 1;
            if (status === 'closed' || status === 'assigned') row.closed += 1;
            else if (status === 'dead') row.dead += 1;
            else row.active += 1;
            if (fee > 0 && (status === 'closed' || status === 'assigned')) {
              const assigneeUid = lead.assignedTo?.uid || null;
              const splitOwner = agentByUid.get(assigneeUid) || agentByUid.get(pipelineUid);
              const split = splitDeal(lead, splitOwner?.defaultPct);
              row.totalFees += split.fee;
              row.totalPayout += split.agentCut;
            }
          }
          submitters = Array.from(submitterMap.values()).sort((a, b) => b.submitted - a.submitted);
        }

        setClosedDeals(closed);
        setActiveDeals(active);
        setDeadDeals(dead);
        setSubmitterBreakdown(submitters);
        setAgentBreakdown(perAgent.sort((x, y) => y.totalAgent - x.totalAgent));
        setByMonth(monthMap);
        setTotals({
          totalFees, totalHomeIQa, totalAgent, dealCount,
          projectedFees, projectedHomeIQa, projectedAgent,
          projectedAgentCut: projectedAgent,
          projectedCount: active.length,
          lostFees, deadCount
        });
      } catch (e) {
        console.error('Finances load failed', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [profile?.uid, profile?.role]);

  // Most recent 12 months sorted oldest → newest for the chart.
  const chartMonths = useMemo(() => {
    const keys = Object.keys(byMonth).sort();
    const last12 = keys.slice(-12);
    return last12.map(k => ({ key: k, ...byMonth[k] }));
  }, [byMonth]);

  const isAdmin = profile?.role === 'admin';
  const isAgent = profile?.role === 'agent';

  if (loading) return <div style={{ padding: 40, color: LIGHT }}>Loading finances…</div>;
  if (!totals) return null;

  // Headline card differs by role.
  const myShare = isAdmin ? totals.totalHomeIQa : totals.totalAgent;
  const myProjected = isAdmin ? totals.projectedHomeIQa : totals.projectedAgent;

  return (
    <>
      <PageHeader
        eyebrow="Finances"
        title={isAdmin ? 'HomeIQa P&L' : <>Your <em style={{ fontStyle: 'italic', color: BURG }}>earnings</em></>}
        sub={isAdmin
          ? 'Company-wide profit, projected pipeline, per-agent breakdown, and dead-pipeline visibility. Driven by your closed assignment fees and the contract-agent splits.'
          : "Every closed deal you sourced, your per-deal payout, and what's still in motion. HomeIQa retains its commission per the default split unless overridden on the lead."}
        right={
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="ghost" onClick={() => navigate('/agent/dashboard')}>← Dashboard</Btn>
          </div>
        }
      />

      {/* Headline tiles */}
      <div style={fs.headlineGrid}>
        <Headline
          eyebrow={isAdmin ? 'HomeIQa share (closed)' : 'You earned (closed)'}
          value={fmtMoney(myShare)}
          sub={`${totals.dealCount} closed deal${totals.dealCount === 1 ? '' : 's'}`}
          accent
        />
        {isAdmin && (
          <Headline
            eyebrow="Paid to contract agents"
            value={fmtMoney(totals.totalAgent)}
            sub={totals.totalFees ? `${fmtMoney(totals.totalFees)} total fees worked` : 'No fees logged yet'}
          />
        )}
        <Headline
          eyebrow={isAdmin ? 'Projected (active)' : 'Projected payout (active)'}
          value={fmtMoney(myProjected)}
          sub={totals.projectedFees ? `Out of ${fmtMoney(totals.projectedFees)} active pipeline` : 'No active pipeline'}
          muted
        />
        <Headline
          eyebrow="Lost (Dead)"
          value={fmtMoney(totals.lostFees)}
          sub={`${totals.deadCount} deal${totals.deadCount === 1 ? '' : 's'} died — ${totals.deadCount > 0 ? 'see below' : 'none yet'}`}
          danger
        />
      </div>

      {/* Monthly trend bar chart */}
      {chartMonths.length > 0 && (
        <Card title={isAdmin ? 'Monthly profit trend' : 'Monthly earnings trend'} right={<Pill color="gold">Last 12 mo</Pill>}>
          <MonthlyChart months={chartMonths} valueKey={isAdmin ? 'homeIQa' : 'agent'} />
        </Card>
      )}

      {/* Per-Lead-Agent (submitter) breakdown — admin only. Tracks who
          is sourcing the deals: counts submitted, active, closed, dead,
          plus total payout. Includes placeholder Lead Agents (link-only). */}
      {isAdmin && submitterBreakdown.length > 0 && (
        <Card title="Leads by Lead Agent" right={<span style={fs.cardEyebrow}>Who is sourcing the deals</span>}>
          <div style={fs.tableWrap}>
            <table style={fs.table}>
              <thead>
                <tr>
                  <th style={fs.th}>Lead Agent</th>
                  <th style={fs.th}>Source</th>
                  <th style={fs.thNum}>Submitted</th>
                  <th style={fs.thNum}>Active</th>
                  <th style={fs.thNum}>Closed</th>
                  <th style={fs.thNum}>Dead</th>
                  <th style={fs.thNum}>Their payout</th>
                </tr>
              </thead>
              <tbody>
                {submitterBreakdown.map(row => (
                  <tr key={row.key}>
                    <td style={fs.td}>
                      <div style={{ fontWeight: 500, color: BURG_DK }}>{row.name}</div>
                      {(row.phone || row.email) && (
                        <div style={{ fontSize: 10, color: LIGHT, marginTop: 2 }}>
                          {[row.phone, row.email].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td style={fs.td}>
                      <Pill color={
                        row.source === 'public_form_share_link' ? 'gold' :
                        row.source === 'public_form' ? 'burg' :
                        row.source === 'manual' ? 'gray' : 'gray'
                      }>
                        {row.source === 'public_form_share_link' ? '🔗 Share link'
                          : row.source === 'public_form' ? 'Public form'
                          : row.source === 'manual' ? 'Direct entry'
                          : row.source}
                      </Pill>
                    </td>
                    <td style={fs.tdNum}>{row.submitted}</td>
                    <td style={fs.tdNum}>{row.active}</td>
                    <td style={{ ...fs.tdNum, color: '#0F6B3F', fontWeight: 600 }}>{row.closed}</td>
                    <td style={{ ...fs.tdNum, color: '#991B1B' }}>{row.dead}</td>
                    <td style={{ ...fs.tdNum, color: BURG_DK, fontWeight: 600 }}>{fmtMoney(row.totalPayout)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Per-agent breakdown (admin only) */}
      {isAdmin && agentBreakdown.length > 0 && (
        <Card title="Per agent" right={<span style={fs.cardEyebrow}>Closed deals · their payout · your cut</span>}>
          <div style={fs.tableWrap}>
            <table style={fs.table}>
              <thead>
                <tr>
                  <th style={fs.th}>Agent</th>
                  <th style={fs.thNum}>Default split</th>
                  <th style={fs.thNum}>Closed deals</th>
                  <th style={fs.thNum}>Total fees</th>
                  <th style={fs.thNum}>Their cut</th>
                  <th style={fs.thNum}>HomeIQa cut</th>
                </tr>
              </thead>
              <tbody>
                {agentBreakdown.map(a => (
                  <tr key={a.uid}>
                    <td style={fs.td}>
                      <div style={{ fontWeight: 500, color: BURG_DK }}>{a.name}</div>
                      <div style={{ fontSize: 10, color: LIGHT, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {a.role === 'admin' ? 'Owner' : 'Contract Agent'}
                      </div>
                    </td>
                    <td style={fs.tdNum}>{Math.round(a.defaultPct * 100)}% to HomeIQa</td>
                    <td style={fs.tdNum}>{a.dealCount}</td>
                    <td style={fs.tdNum}>{fmtMoney(a.totalFees)}</td>
                    <td style={{ ...fs.tdNum, color: BURG_DK, fontWeight: 600 }}>{fmtMoney(a.totalAgent)}</td>
                    <td style={{ ...fs.tdNum, color: '#0F6B3F', fontWeight: 600 }}>{fmtMoney(a.totalHomeIQa)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Closed deals */}
      <Card title={`Closed deals (${closedDeals.length})`} right={<span style={fs.cardEyebrow}>{isAdmin ? 'All-time revenue' : 'All-time payouts'}</span>}>
        {closedDeals.length === 0 ? (
          <EmptyState message="No closed deals yet — they'll show up here the moment a lead's status flips to Assigned or Closed." />
        ) : (
          <DealTable deals={closedDeals} valueLabel={isAdmin ? 'HomeIQa cut' : 'Your cut'} valueKey={isAdmin ? 'homeIQaCut' : 'agentCut'} navigate={navigate} />
        )}
      </Card>

      {/* Active deals */}
      <Card title={`In motion (${activeDeals.length})`} right={<span style={fs.cardEyebrow}>Negotiating + Under Contract</span>}>
        {activeDeals.length === 0 ? (
          <EmptyState message="Nothing in motion right now. Deals show up here once status moves to Negotiating or Under Contract with an assignment fee on the calc." />
        ) : (
          <DealTable deals={activeDeals} valueLabel={isAdmin ? 'Projected HomeIQa' : 'Projected payout'} valueKey={isAdmin ? 'homeIQaCut' : 'agentCut'} navigate={navigate} />
        )}
      </Card>

      {/* Dead deals */}
      <Card title={`Lost — Dead (${deadDeals.length})`} right={<span style={fs.cardEyebrow}>What walked away</span>}>
        {deadDeals.length === 0 ? (
          <EmptyState message="No dead deals — everything you've worked is still alive or already closed." />
        ) : (
          <DealTable deals={deadDeals} valueLabel="Walked-away fee" valueKey="agentCut" navigate={navigate} muted />
        )}
      </Card>
    </>
  );
}

function Headline({ eyebrow, value, sub, accent, muted, danger }) {
  return (
    <div style={{
      ...fs.tile,
      ...(accent ? fs.tileAccent : {}),
      ...(muted ? fs.tileMuted : {}),
      ...(danger ? fs.tileDanger : {})
    }}>
      <div style={fs.tileLabel}>{eyebrow}</div>
      <div style={fs.tileValue}>{value}</div>
      <div style={fs.tileSub}>{sub}</div>
    </div>
  );
}

function MonthlyChart({ months, valueKey }) {
  const max = Math.max(1, ...months.map(m => m[valueKey] || 0));
  return (
    <div style={fs.chart}>
      {months.map(m => {
        const v = m[valueKey] || 0;
        const pct = (v / max) * 100;
        const dt = new Date(`${m.key}-01T00:00:00`);
        return (
          <div key={m.key} style={fs.chartCol}>
            <div style={fs.chartBarWrap}>
              <div style={{ ...fs.chartBar, height: `${pct}%` }} title={fmtMoney(v)} />
            </div>
            <div style={fs.chartValue}>{v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${Math.round(v)}`}</div>
            <div style={fs.chartLabel}>{dt.toLocaleDateString([], { month: 'short' })}{dt.getMonth() === 0 ? ` '${String(dt.getFullYear()).slice(2)}` : ''}</div>
          </div>
        );
      })}
    </div>
  );
}

function DealTable({ deals, valueLabel, valueKey, navigate, muted }) {
  return (
    <div style={fs.tableWrap}>
      <table style={fs.table}>
        <thead>
          <tr>
            <th style={fs.th}>Property</th>
            <th style={fs.th}>Status</th>
            <th style={fs.th}>Worked by</th>
            <th style={fs.thNum}>Fee</th>
            <th style={fs.thNum}>{valueLabel}</th>
            <th style={fs.thNum}>When</th>
          </tr>
        </thead>
        <tbody>
          {deals.map(d => (
            <tr
              key={`${d._ownerUid}-${d.id}`}
              onClick={() => navigate(`/agent/leads/${d.id}`)}
              style={{ cursor: 'pointer', opacity: muted ? 0.7 : 1 }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#FAF7F2'}
              onMouseLeave={(e) => e.currentTarget.style.background = ''}
            >
              <td style={fs.td}>
                <div style={{ fontWeight: 500, color: BURG_DK }}>{d.address?.line1 || '(no address)'}</div>
                <div style={{ fontSize: 10, color: LIGHT }}>
                  {[d.address?.city, d.address?.state].filter(Boolean).join(', ')}
                </div>
              </td>
              <td style={fs.td}><Pill color={
                d.status === 'Closed' ? 'green' :
                d.status === 'Assigned' ? 'green' :
                d.status === 'Dead' ? 'red' :
                'gold'
              }>{d.status}</Pill></td>
              <td style={fs.td}>{d._ownerName}</td>
              <td style={fs.tdNum}>{fmtMoney(d.fee)}</td>
              <td style={{ ...fs.tdNum, color: BURG_DK, fontWeight: 600 }}>{fmtMoney(d[valueKey])}</td>
              <td style={fs.tdNum}>
                {d.closedAt ? new Date(d.closedAt).toLocaleDateString() : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div style={{ padding: '24px 16px', color: LIGHT, fontSize: 13, lineHeight: 1.6, textAlign: 'center' }}>
      {message}
    </div>
  );
}

const fs = {
  headlineGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 22 },
  tile: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 4 },
  tileAccent: { background: 'linear-gradient(135deg, rgba(107,30,46,0.04), rgba(201,168,76,0.08))', border: `1px solid rgba(201,168,76,0.4)` },
  tileMuted: { background: CREAM, opacity: 0.85 },
  tileDanger: { background: '#FEF2F2', border: '1px solid #FCA5A5' },
  tileLabel: { fontSize: 9, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: LIGHT },
  tileValue: { fontFamily: "'Cormorant Garamond', serif", fontSize: 30, fontWeight: 600, color: BURG_DK, lineHeight: 1, marginTop: 4 },
  tileSub: { fontSize: 11, color: MID, marginTop: 2, lineHeight: 1.4 },

  cardEyebrow: { fontSize: 11, color: LIGHT, fontStyle: 'italic', letterSpacing: '0.04em' },

  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { textAlign: 'left', padding: '10px 12px', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: LIGHT, borderBottom: `1px solid ${BORDER}`, fontWeight: 500 },
  thNum: { textAlign: 'right', padding: '10px 12px', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: LIGHT, borderBottom: `1px solid ${BORDER}`, fontWeight: 500 },
  td: { padding: '12px', borderBottom: `1px solid ${BORDER}`, color: '#1C1C1C', verticalAlign: 'top' },
  tdNum: { padding: '12px', borderBottom: `1px solid ${BORDER}`, color: '#1C1C1C', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },

  chart: { display: 'flex', alignItems: 'flex-end', gap: 6, height: 220, padding: '12px 0' },
  chartCol: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 30 },
  chartBarWrap: { width: '100%', height: 150, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  chartBar: { width: '70%', minHeight: 2, background: `linear-gradient(180deg, ${GOLD}, ${BURG})`, borderRadius: '4px 4px 0 0', transition: 'height 0.3s ease' },
  chartValue: { fontSize: 10, color: BURG_DK, fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  chartLabel: { fontSize: 9, color: LIGHT, textTransform: 'uppercase', letterSpacing: '0.08em' }
};
